import type React from 'react'
import { Area, AreaChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import styled from 'styled-components'
import { theme } from '../../theme/theme'

interface WBalPoint {
  time: number
  wBal: number
  power: number
}

interface WBalChartProps {
  wBal: WBalPoint[]
  wPrime: number
  cp: number
}

const Container = styled.div`
  width: 100%;

  /* Remove blue outline on chart elements when clicked */
  .recharts-surface {
    outline: none;
  }

  .recharts-wrapper {
    outline: none;
  }
`

const LoadingMessage = styled.div`
  padding: ${theme.spacing.lg};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${theme.typography.sm};
`

const StatsContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.lg};
  justify-content: center;
  margin-top: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  flex-wrap: wrap;

  @media (max-width: 640px) {
    gap: ${theme.spacing.sm};
    padding: ${theme.spacing.sm};
  }
`

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.xs};
`

const StatLabel = styled.div`
  font-size: ${theme.typography.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 500;
`

const StatValue = styled.div<{ $color?: string }>`
  font-size: ${theme.typography.lg};
  font-weight: 600;
  color: ${({ $color, theme }) => $color || theme.colors.text};
`

// Helper function to format time as mm:ss
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Helper function to format joules to kJ
const formatKJ = (joules: number): string => {
  return `${(joules / 1000).toFixed(1)} kJ`
}

// Custom tooltip component
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    return (
      <div
        style={{
          background: theme.colors.cardBackground,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.borderRadius.sm,
          padding: theme.spacing.sm,
          fontSize: theme.typography.xs,
          color: theme.colors.text,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: theme.spacing.xs }}>{formatTime(data.time)}</div>
        <div>W'bal: {formatKJ(data.wBal)}</div>
        <div>Power: {data.power}W</div>
      </div>
    )
  }
  return null
}

export const WBalChart: React.FC<WBalChartProps> = ({ wBal, wPrime, cp }) => {
  if (!wBal || wBal.length === 0) {
    return (
      <Container>
        <LoadingMessage>No W'bal data available for this workout.</LoadingMessage>
      </Container>
    )
  }

  // Calculate statistics
  const minWBal = Math.min(...wBal.map((p) => p.wBal))
  const minWBalPercent = (minWBal / wPrime) * 100

  // Find time when W'bal was lowest
  const minWBalPoint = wBal.find((p) => p.wBal === minWBal)
  const minWBalTime = minWBalPoint ? minWBalPoint.time : 0

  // Check if W'bal reached zero (fully depleted)
  const depleted = minWBal <= 0

  // Calculate area color based on W'bal level
  const getWBalColor = (wBalValue: number): string => {
    const percentage = (wBalValue / wPrime) * 100
    if (percentage < 10) return '#ef4444' // Red - critically low
    if (percentage < 30) return '#f59e0b' // Orange - low
    if (percentage < 60) return '#fbbf24' // Yellow - moderate
    return '#10b981' // Green - good
  }

  return (
    <Container>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={wBal} margin={{ top: 30, right: 30, left: 20, bottom: 5 }}>
          <defs>
            <linearGradient id="wBalGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity={0.8} />
              <stop offset="60%" stopColor="#fbbf24" stopOpacity={0.6} />
              <stop offset="100%" stopColor="#ef4444" stopOpacity={0.4} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={theme.colors.border} strokeDasharray="3 3" />
          <XAxis
            dataKey="time"
            stroke={theme.colors.textSecondary}
            tick={{ fill: theme.colors.textSecondary, fontSize: 10 }}
            tickFormatter={(value) => formatTime(value)}
            label={{
              value: 'Time',
              position: 'insideBottom',
              offset: -5,
              fill: theme.colors.text,
              fontSize: 12,
            }}
          />
          <YAxis
            stroke={theme.colors.textSecondary}
            tick={{ fill: theme.colors.textSecondary, fontSize: 10 }}
            label={{
              value: "W'bal (kJ)",
              angle: -90,
              position: 'insideLeft',
              fill: theme.colors.text,
              fontSize: 12,
            }}
            tickFormatter={(value) => (value / 1000).toFixed(1)}
            domain={[0, wPrime]}
          />
          {/*
          <YAxis
            key="power"
            yAxisId="power"
            stroke={theme.colors.textSecondary}
            tick={{ fill: theme.colors.textSecondary, fontSize: 10 }}
            domain={[0, 'auto']}
            label={{
              value: 'Power (W)',
              angle: -90,
              position: 'insideLeft',
              fill: theme.colors.text,
              fontSize: 12,
            }}
          />
          <Area
            type="monotone"
            dataKey="power"
            stroke="#F59E0B"
            fill="#F59E0B"
            strokeWidth={1}
            name={'power'}
            unit={'W'}
            yAxisId="power"
          />
          */}

          <Tooltip content={<CustomTooltip />} />
          <Area type="monotone" dataKey="wBal" stroke="#10b981" strokeWidth={2} fill="url(#wBalGradient)" />

          {/* Reference line at W' (full capacity) */}
          <ReferenceLine
            y={wPrime}
            stroke="#10b981"
            strokeWidth={1}
            strokeDasharray="5 5"
            opacity={0.5}
            label={{
              value: `W' (${formatKJ(wPrime)})`,
              position: 'right',
              fill: '#10b981',
              fontSize: 11,
              fontWeight: 500,
            }}
          />

          {/* Reference line at 0 (depletion) */}
          <ReferenceLine
            y={0}
            stroke="#ef4444"
            strokeWidth={2}
            label={{
              value: 'Depletion',
              position: 'right',
              fill: '#ef4444',
              fontSize: 11,
              fontWeight: 600,
            }}
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* Statistics */}
      <StatsContainer>
        <StatItem>
          <StatLabel>CP (Critical Power)</StatLabel>
          <StatValue>{cp}W</StatValue>
        </StatItem>
        <StatItem>
          <StatLabel>W' (Anaerobic Capacity)</StatLabel>
          <StatValue>{formatKJ(wPrime)}</StatValue>
        </StatItem>
        <StatItem>
          <StatLabel>Minimum W'bal</StatLabel>
          <StatValue $color={getWBalColor(minWBal)}>
            {formatKJ(minWBal)} ({minWBalPercent.toFixed(0)}%)
          </StatValue>
        </StatItem>
        {/*<StatItem>
          <StatLabel>Lowest at</StatLabel>
          <StatValue>{formatTime(minWBalTime)}</StatValue>
        </StatItem>*/}
      </StatsContainer>
    </Container>
  )
}
